import { showRequestError } from '../../utils/request-feedback';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Descriptions, Drawer, Form, Segmented, Input, Popconfirm, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getPermissionCatalog, listPermissionRoles, listPermissionUsers, savePermissionRole, savePermissionUser, deletePermissionRole, deletePermissionUser } from '../../api/permission';
import PermissionAssignment from '../../permissions/assignment';
import { mergeBindings } from '../../permissions/catalog';
import { usePermissions, permissionChanged } from '../../permissions/access';
import '../../permissions/permission.css';

export default function PermissionAccounts({ kind = 'user' }) {
    const roleMode = kind === 'role';
    const title = roleMode ? 'Roles' : 'Admins';
    const { can } = usePermissions();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [source, setSource] = useState('all');
    const [catalog, setCatalog] = useState(null);
    const [roles, setRoles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const prefix = `permission.${kind}`;
    const loadVersion = useRef(0);
    const load = useCallback(async () => {
        const version = ++loadVersion.current;
        setLoading(true);
        try {
            const result = await (roleMode ? listPermissionRoles : listPermissionUsers)({ keyword, page, size: 20 });
            if (version !== loadVersion.current) return;
            setRows(result.list || []); setTotal(Number(result.total || 0));
        } catch (err) { if (version === loadVersion.current) showRequestError(err); }
        finally { if (version === loadVersion.current) setLoading(false); }
    }, [roleMode, keyword, page]);
    useEffect(() => { load(); return () => { loadVersion.current++; }; }, [load]);
    const open = async (record = {}, readOnly = Boolean(record.id)) => {

        try {
            const document = await getPermissionCatalog();
            const loadedCatalog = JSON.parse(document.content);
            setCatalog(loadedCatalog);
            let options = roles;
            if (!roleMode) {
                options = []; let nextPage = 1; let count = 1;
                while (options.length < count) {
                    const response = await listPermissionRoles({ size: 200, page: nextPage++ });
                    const batch = response.list || []; options.push(...batch); count = Number(response.total || 0);
                    if (!batch.length) break;
                }
                setRoles(options);
            }
            const defaults = { ...record, readOnly, bindings: record.bindings || [], roleId: record.roleId && String(record.roleId) !== '0' ? record.roleId : undefined, disabled: record.disabled || false };
            setEditing(defaults); setSnapshot(defaults); setSource('all'); form.resetFields(); form.setFieldsValue(defaults);
        } catch (err) { showRequestError(err); }
    };
    const save = async () => {
        try {
            const fields = await form.validateFields();
            setSaving(true);
            const payload = { ...fields, id: editing.id || '0', ...(!roleMode ? { roleId: fields.roleId || '0' } : {}), expectedRevision: editing.revision || '0', bindings: editing.bindings || [] };
            const saved = await (roleMode ? savePermissionRole : savePermissionUser)(payload);
            const detail = { ...saved, ...(!roleMode ? { roleId: saved.roleId && String(saved.roleId) !== '0' ? saved.roleId : undefined } : {}), readOnly: true }; setEditing(detail); setSnapshot(detail); setSource('all'); form.setFieldsValue(detail);
            message.success('已保存，权限立即生效'); permissionChanged(); await load();
        } catch (err) { if (!err.errorFields) showRequestError(err); }
        finally { setSaving(false); }
    };
    const remove = async (record) => {
        try { await (roleMode ? deletePermissionRole : deletePermissionUser)(record.id, record.revision); message.success('已删除'); permissionChanged(); await load(); }
        catch (err) { showRequestError(err); }
    };
    const columns = roleMode ? [
        { title: '名称', dataIndex: 'name' },
        { title: '角色标识', dataIndex: 'word' },
    ] : [
        { title: '账号邮箱', dataIndex: 'username' },
        { title: '角色', dataIndex: 'roleWord', render: value => <Tag>{value || '无角色'}</Tag> },
        { title: '状态', dataIndex: 'disabled', render: value => <Tag color={value ? 'default' : 'success'}>{value ? '已禁用' : '正常'}</Tag> },
    ];
    columns.push({ title: '备注', dataIndex: 'remark' }, {
        title: '操作', key: 'actions', width: 160,
        render: (_, record) => <Space>
            <Button type="link" onClick={() => open(record)}>详情</Button>
            {can(`${prefix}.delete`) && <Popconfirm title="确认删除？" description={roleMode ? "关联账号将解除角色关联并失去角色授权，账号直接授权保留。" : "删除后该记录的授权会立即失效。"} onConfirm={() => remove(record)}><Button type="link" danger>删除</Button></Popconfirm>}
        </Space>,
    });
    const selectedRole = roles.find(role => String(role.id) === String(editing?.roleId));
    const inherited = roleMode ? [] : selectedRole?.bindings || [];
    const detailBindings = source === 'direct' || roleMode ? editing?.bindings || [] : source === 'role' ? selectedRole?.bindings || [] : mergeBindings(editing?.bindings || [], inherited);
    const cancelEdit = () => { setEditing(snapshot); form.setFieldsValue(snapshot); };
    return <main className="permission-page">
        <div className="permission-page-header">
            <div><Typography.Title level={3}>{title}</Typography.Title><p>{roleMode ? '按资源、操作和业务按钮配置角色权限。' : '使用 Firebase 登录邮箱识别账号，支持单角色与直接授权。'}</p></div>
            {can(`${prefix}.create`) && <Button type="primary" icon={<PlusOutlined />} onClick={() => open()}>新增{roleMode ? '角色' : '账号'}</Button>}
        </div>
        <Input.Search aria-label="搜索账号或角色" placeholder={roleMode ? '搜索角色名称或标识' : '搜索账号邮箱'} allowClear onSearch={value => { setKeyword(value); setPage(1); }} style={{ maxWidth: 360, marginBottom: 16 }} />
        <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} pagination={{ current: page, pageSize: 20, total, onChange: setPage, showSizeChanger: false }} scroll={{ x: 700 }} />
        <Drawer title={`${roleMode ? '角色' : '账号'}${editing?.readOnly ? '详情' : editing?.id ? '编辑' : '新增'}`} width="min(1240px, 96vw)" open={Boolean(editing)} onClose={() => !saving && setEditing(null)} destroyOnClose extra={editing?.readOnly ? can(`${prefix}.update`) && <Button type="primary" onClick={() => { setSnapshot(editing); setEditing(current => ({ ...current, readOnly: false })); }}>编辑</Button> : <Space>{editing?.id && <Button disabled={saving} onClick={cancelEdit}>取消编辑</Button>}<Button type="primary" loading={saving} onClick={save}>保存</Button></Space>}>
            {editing?.readOnly && <Descriptions bordered size="small" column={2} items={[
                { key: 'identity', label: roleMode ? '角色名称' : '账号邮箱', children: roleMode ? editing.name : editing.username },
                { key: 'role', label: roleMode ? '角色标识' : '角色', children: roleMode ? editing.word : selectedRole?.name || editing.roleWord || '无角色' },
                ...(!roleMode ? [{ key: 'status', label: '状态', children: editing.disabled ? '已禁用' : '正常' }] : []),
                { key: 'remark', label: '备注', children: editing.remark || '—' },
            ]} />}
            <Form name={`permission-${kind}`} style={{ display: editing?.readOnly ? 'none' : undefined }} form={form} layout="vertical" disabled={editing?.readOnly || saving} onValuesChange={(_, fields) => setEditing(current => ({ ...current, ...fields }))}>
                {roleMode ? <Space align="start" style={{ width: '100%' }}>
                    <Form.Item name="name" label="角色名称" rules={[{ required: true, whitespace: true }]}><Input maxLength={255} /></Form.Item>
                    <Form.Item name="word" label="角色标识" rules={[{ required: true, whitespace: true }]}><Input maxLength={128} /></Form.Item>
                </Space> : <Space align="start" wrap>
                    <Form.Item name="username" label="账号邮箱（Username）" rules={[{ required: true, type: 'email', message: '请输入有效的 Firebase 登录邮箱' }]}><Input style={{ width: 320 }} autoComplete="off" /></Form.Item>
                    <Form.Item name="roleId" label="角色"><Select allowClear placeholder="不分配角色" style={{ width: 220 }} options={roles.map(role => ({ value: role.id, label: `${role.name} (${role.word})` }))} /></Form.Item>
                    <Form.Item name="disabled" label="禁用账号" valuePropName="checked"><Switch /></Form.Item>
                </Space>}
                <Form.Item name="remark" label="备注"><Input.TextArea rows={2} maxLength={1024} /></Form.Item>
            </Form>
            <Typography.Title level={5}>{roleMode ? '角色权限' : editing?.readOnly ? '权限详情' : '账号直接授权'}</Typography.Title>
            {!roleMode && editing?.readOnly && <Segmented aria-label="权限来源" value={source} onChange={setSource} options={[{ value: 'all', label: '全部' }, { value: 'direct', label: '账号直接授权' }, { value: 'role', label: '角色继承' }]} />}
            {!roleMode && editing?.readOnly && source === 'role' && <Typography.Text type="secondary" className="permission-source-name">角色：{selectedRole?.name || editing.roleWord || '无角色'}</Typography.Text>}
            <PermissionAssignment subject={roleMode ? 'role' : 'user'} catalog={catalog} value={editing?.readOnly ? detailBindings : editing?.bindings || []} inherited={editing?.readOnly ? [] : inherited} readOnly={editing?.readOnly} disabled={saving} editableValue={editing?.bindings || []} onEdit={can(`${prefix}.update`) && (roleMode || source !== 'role') ? bindings => {
                setSnapshot(editing);
                setEditing(current => ({ ...current, bindings, readOnly: false }));
                setSource('direct');
            } : undefined} onChange={bindings => setEditing(current => ({ ...current, bindings }))} />
        </Drawer>
    </main>;
}
